<template>
  <UserProfile>
    <InfoList
      :title="$t('pages.user_profile.profile.personal_info')"
      :data="data.personalDetails"
      button-data-testid="open-form--edit-personal-info"
      :on-click="isApplicationEditable && openPersonalDetailsEditModal"/>
    <InfoList
      :title="$t('pages.user_profile.profile.business_info')"
      :data="data.businessInfo"
      button-data-testid="open-form--edit-business-info"
      :on-click="isApplicationEditable && openBusinessInfoEditModal"/>
    <SoleTraderDocumentList/>
    <ReviewedDocumentsList
      v-if="store.reviewedDocuments?.length"
      :documents="store.reviewedDocuments"
      :download-file="OnboardingAdminRequests.SoleTrader.getReviewedDocument"/>
    <OnboardingCommentList :application-id="applicationId"/>

    <PersonalDetailsForm
      v-if="isModalOpen.personalDetails"
      v-model:is-modal-open="isModalOpen.personalDetails"/>
    <BusinessInfoForm
      v-if="isModalOpen.businessInfo"
      v-model:is-modal-open="isModalOpen.businessInfo"/>
  </UserProfile>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import InfoList from '@/components/InfoList.vue';
import {
  getAccountTypeString,
  getCountryName, getDate, getPersonalCountryName, transformDashboardAddress,
} from '@/modules/onboarding/utils';
import OnboardingCommentList from '@/modules/onboarding-dashboard/shared/components/OnboardingCommentList.vue';
import { useCustomerProfileInject } from '@/modules/onboarding-dashboard/shared/utils';
import BusinessInfoForm from '@/modules/onboarding-dashboard/SoleTrader/components/BusinessInfoForm.vue';
import PersonalDetailsForm from '@/modules/onboarding-dashboard/SoleTrader/components/PersonalDetailsForm.vue';
import SoleTraderDocumentList from '@/modules/onboarding-dashboard/SoleTrader/components/SoleTraderDocumentList.vue';
import ReviewedDocumentsList from '@/modules/onboarding-user-profile/components/ReviewedDocumentsList.vue';
import UserProfile from '@/modules/onboarding-user-profile/components/user-profile.vue';
import { OnboardingAdminRequests } from '@/services/requests';
import { useOnboardingAdminSoleTraderStore } from '@/stores/onboarding/OnboardingAdminSoleTrader';
import { getRouteParam } from '@/utils/getRouteParam';

const i18n = useI18n();
const route = useRoute();

const store = useOnboardingAdminSoleTraderStore();
const { isApplicationEditable } = useCustomerProfileInject();

const applicationId = getRouteParam(route.params.id);

const isModalOpen = reactive({
  personalDetails: false,
  businessInfo: false,
});

function openPersonalDetailsEditModal() {
  isModalOpen.personalDetails = true;
}
function openBusinessInfoEditModal() {
  isModalOpen.businessInfo = true;
}

const data = computed(() => ({
  personalDetails: [
    {
      label: i18n.t('form.label.account_type'),
      value: i18n.t(getAccountTypeString(store.applicationType)),
    },
    {
      label: i18n.t('form.label.nationality'),
      value: getPersonalCountryName(store.personalDetails?.nationalityIso3),
    },
    {
      label: i18n.t('form.label.date_of_birth'),
      value: getDate(store.personalDetails?.dateOfBirth),
    },
    {
      label: i18n.t('form.label.registered_address'),
      value: transformDashboardAddress(store.personalDetails?.residentialAddress),
    },
    {
      label: i18n.t('form.label.mailing_address'),
      value: transformDashboardAddress(store.personalDetails?.mailingAddress),
    },
    {
      label: i18n.t('form.label.account_created'),
      value: getDate(store.createAt),
    },
  ],
  businessInfo: [
    {
      label: i18n.t('form.label.country_of_registration'),
      value: getCountryName(store.businessInfo?.countryIso3),
    },
    {
      label: i18n.t('form.label.starting_date_of_trading_activity'),
      value: getDate(store.businessInfo?.dateOfBusinessStart),
    },
    {
      label: i18n.t('form.label.business_name'),
      value: store.businessInfo?.businessLegalName,
    },
    {
      label: i18n.t('form.label.vat_number'),
      value: store.businessInfo?.vatNumber,
    },
    {
      label: i18n.t('form.label.industry'),
      value: store.businessInfo?.businessIndustry
        ? i18n.t(`entity.BusinessIndustry.${store.businessInfo?.businessIndustry}`)
        : undefined,
    },
    {
      label: i18n.t('form.label.business_website'),
      value: store.businessInfo?.website,
    },
    {
      label: i18n.t('form.label.email'),
      value: store.businessInfo?.email,
    },
    {
      label: i18n.t('form.label.business_description'),
      value: store.businessInfo?.businessActivityDescription,
    },
    {
      label: i18n.t('pages.onboarding.admin_page.estimated_annual_turnover'),
      value: store.businessInfo?.annualTurnover
        ? i18n.t(`entity.AnnualTurnover.${store.businessInfo?.annualTurnover}`)
        : undefined,
    },
  ],
}));
</script>
